import { Link, useParams } from "react-router-dom";
import { LEARN_MODULES } from "@/data/learn";
import { ArrowLeft, Clock } from "lucide-react";

export default function LearnArticle() {
  const { slug } = useParams();
  // Handle common misspelling: /learn/whiskey-101 → /learn/whisky-101
  const normalizedSlug = (slug || "").replace(/^whiskey-/, "whisky-").replace(/^whisky-101$/, "whisky-101");
  const module = LEARN_MODULES.find(m => m.slug === normalizedSlug);

  if (!module) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <p className="font-serif-display text-3xl">Article not found.</p>
        <Link to="/learn" className="btn-pill btn-pink mt-6 inline-flex">Back to Learn</Link>
      </div>
    );
  }

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16" data-testid={`learn-article-${slug}`}>
      <Link to="/learn" className="inline-flex items-center gap-2 text-sm font-semibold text-cream/70 hover:text-pink-brand" data-testid="back-to-learn">
        <ArrowLeft size={16} /> All modules
      </Link>
      <p className="eyebrow mt-6">{module.category}</p>
      <h1 className="mt-2 font-serif-display text-4xl sm:text-5xl md:text-6xl font-bold text-cream leading-[1.05]">{module.title}</h1>
      <p className="mt-4 flex items-center gap-2 text-sm text-cream/60"><Clock size={14} /> {module.read} read</p>

      <div className="mt-8 aspect-[16/9] rounded-2xl overflow-hidden">
        <img src={module.image} alt={module.title} className="w-full h-full object-cover" />
      </div>

      <div className="mt-10 space-y-10 font-body text-base sm:text-lg text-cream/85 leading-relaxed">
        {module.body.map((s, i) => (
          <section key={i}>
            {s.heading && <h2 className="font-serif-display text-2xl sm:text-3xl font-bold text-cream mb-4 leading-tight">{s.heading}</h2>}
            {s.paragraphs && s.paragraphs.map((p, k) => <p key={k} className="mb-4">{p}</p>)}
            {s.bullets && (
              <dl className="mt-2 divide-y divide-cream/10 rounded-2xl border border-cream/10 bg-ink-panel">
                {s.bullets.map((b, k) => (
                  <div key={k} className="p-4 sm:p-5 grid sm:grid-cols-[minmax(140px,180px)_1fr] gap-2 sm:gap-6">
                    <dt className="text-pink-brand font-semibold font-serif-display text-lg leading-tight">{b.term}</dt>
                    <dd className="text-cream/85 text-base leading-relaxed">{b.def}</dd>
                  </div>
                ))}
              </dl>
            )}
          </section>
        ))}
      </div>

      <div className="mt-14 card-ink p-6 sm:p-8 rounded-2xl">
        <p className="font-serif-editor italic text-cream/80 text-lg">"The best glass of anything is the one you understand."</p>
        <p className="eyebrow mt-3" style={{ color: "#F9C0D2" }}>— Sip Alchemy</p>
      </div>

      <div className="mt-10 flex flex-col sm:flex-row gap-3">
        <Link to="/drink-finder" className="btn-pill btn-pink" data-testid="article-cta-finder">Take the Drink Finder</Link>
        <Link to="/learn" className="btn-pill btn-outline-ink" data-testid="article-cta-more">More modules</Link>
      </div>
    </article>
  );
}
